import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@services/api';
import { Card, Button, Input, NumberInput, Badge, Icon } from '@atoms/index';
import { OperatingHoursForm } from '@organisms/index';
import { MapLibreMap, MapMarker, CuisineMultiSelect } from '@molecules/index';
import { useRestaurantStore } from '@store/restaurantStore';
import { Toast } from '@atoms/index';
import { formatPrice } from '@/utils/format';
import { cn } from '@/utils/cn';
import { geocodingService, type GeocodingResult } from '@services/geocodingService';
import { compressImage } from '@utils/compressImage';
import type { Map } from 'maplibre-gl';

/**
 * SettingsTab - Restaurant settings and information management
 *
 * Per Restaurant Management Panel: Settings tab
 * Features:
 * - Restaurant info form (name, description, phone, address)
 * - Logo upload (with preview)
 * - Banner upload (with preview)
 * - Toggle accepting orders switch
 * - Service zone display (read-only)
 */
const SettingsTab = () => {
  const {
    restaurant,
    isLoadingRestaurant,
    error: restaurantError,
    loadRestaurant,
    updateRestaurant,
    uploadLogo,
    uploadBanner,
    toggleAcceptingOrders,
    clearError,
  } = useRestaurantStore();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    address: '',
    latitude: null as number | null,
    longitude: null as number | null,
    minimumOrderAmount: 0,
    deliveryFee: 0,
    estimatedPreparationMinutes: 30,
    cuisineCategoryIds: [] as string[],
  });

  const [cuisineCategories, setCuisineCategories] = useState<
    Array<{ id: string; name: string; imageUrl: string | null }>
  >([]);

  const [mapPosition, setMapPosition] = useState<[number, number] | null>(null);
  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const { loadWorkingHours } = useRestaurantStore();

  const defaultCenter: [number, number] = [35.3308, 36.9914];

  useEffect(() => {
    if (restaurant?.latitude && restaurant?.longitude) {
      const lat = Number(restaurant.latitude);
      const lng = Number(restaurant.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapPosition([lng, lat]);
        setTimeout(() => setIsMapReady(true), 100);
      }
    } else {
      setMapPosition(defaultCenter);
      setTimeout(() => setIsMapReady(true), 100);
    }
  }, [restaurant]);

  useEffect(() => {
    if (mapPosition) {
      setFormData((prev) => ({
        ...prev,
        latitude: mapPosition[1],
        longitude: mapPosition[0],
      }));
    }
  }, [mapPosition]);

  const reverseGeocode = useCallback(async (latitude: number, longitude: number) => {
    setIsReverseGeocoding(true);
    try {
      const result = await geocodingService.reverseGeocode(latitude, longitude);
      
      if (result) {
        let finalDistrict = result.district;
        
        if (!finalDistrict && result.fullAddress) {
          const addressParts = result.fullAddress.split(',').map(s => s.trim());
          const turkishDistrictKeywords = ['Mahallesi', 'Mahalle', 'İlçe', 'ilçe'];
          
          for (const part of addressParts) {
            if (part && part !== result.city && part !== 'Türkiye' && part !== 'Turkey') {
              const postcodeDistrictMatch = part.match(/^\d{5}\s+([^/]+)\/(.+)$/);
              if (postcodeDistrictMatch) {
                finalDistrict = postcodeDistrictMatch[1].trim();
                break;
              }
              
              const districtCityMatch = part.match(/^([^/]+)\/(.+)$/);
              if (districtCityMatch && !part.match(/^\d{5}/)) {
                finalDistrict = districtCityMatch[1].trim();
                break;
              }
              
              if (turkishDistrictKeywords.some(keyword => part.includes(keyword))) {
                finalDistrict = part.replace(/Mahallesi|Mahalle|İlçe|ilçe/g, '').trim();
                break;
              }
              
              if (!part.match(/^\d{5}/) && !part.includes('Sk.') && !part.includes('Sokak') && !part.includes('Cadde') && !part.includes('Bulvar') && !part.includes('No:')) {
                if (part.length > 2 && part.length < 30) {
                  finalDistrict = part;
                  break;
                }
              }
            }
          }
        }

        setFormData((prev) => ({
          ...prev,
          city: result.city || prev.city || '',
          district: finalDistrict || prev.district || '',
          fullAddress: result.fullAddress || prev.address || '',
          address: result.fullAddress || prev.address || '',
          latitude: result.latitude || prev.latitude || 0,
          longitude: result.longitude || prev.longitude || 0,
        }));
      }
    } catch (error: any) {
      console.error('Reverse geocoding failed:', error);
      const errorMessage = error.response?.data?.error?.message || error.message || 'Adres bilgisi alınamadı';
      if (errorMessage.includes('API key') || errorMessage.includes('access token') || errorMessage.includes('not configured')) {
        console.warn('Geocoding service not configured. Please configure API keys in backend .env file.');
      }
    } finally {
      setIsReverseGeocoding(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    setError(null);
    try {
      const results = await geocodingService.searchAddresses(query);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error: any) {
      console.error('Geocoding search failed:', error);
      const errorMessage = error.response?.data?.error?.message || error.message || 'Adres arama başarısız oldu';
      if (errorMessage.includes('API key') || errorMessage.includes('access token') || errorMessage.includes('not configured')) {
        setError('Adres arama servisi yapılandırılmamış. Lütfen yönetici ile iletişime geçin.');
      } else {
        setError(errorMessage);
      }
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(searchQuery);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddressSelect = (result: GeocodingResult) => {
    let finalDistrict = result.district;
    
    if (!finalDistrict && result.fullAddress) {
      const addressParts = result.fullAddress.split(',').map(s => s.trim());
      const turkishDistrictKeywords = ['Mahallesi', 'Mahalle', 'İlçe', 'ilçe'];
      
      for (const part of addressParts) {
        if (part && part !== result.city && part !== 'Türkiye' && part !== 'Turkey') {
          const postcodeDistrictMatch = part.match(/^\d{5}\s+([^/]+)\/(.+)$/);
          if (postcodeDistrictMatch) {
            finalDistrict = postcodeDistrictMatch[1].trim();
            break;
          }
          
          const districtCityMatch = part.match(/^([^/]+)\/(.+)$/);
          if (districtCityMatch && !part.match(/^\d{5}/)) {
            finalDistrict = districtCityMatch[1].trim();
            break;
          }
          
          if (turkishDistrictKeywords.some(keyword => part.includes(keyword))) {
            finalDistrict = part.replace(/Mahallesi|Mahalle|İlçe|ilçe/g, '').trim();
            break;
          }
          
          if (!part.match(/^\d{5}/) && !part.includes('Sk.') && !part.includes('Sokak') && !part.includes('Cadde') && !part.includes('Bulvar') && !part.includes('No:')) {
            if (part.length > 2 && part.length < 30) {
              finalDistrict = part;
              break;
            }
          }
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      city: result.city || prev.city || '',
      district: finalDistrict || prev.district || '',
      fullAddress: result.fullAddress || result.displayName || prev.address || '',
      address: result.fullAddress || result.displayName || prev.address || '',
      latitude: result.latitude || prev.latitude || 0,
      longitude: result.longitude || prev.longitude || 0,
    }));

    setMapPosition([result.longitude, result.latitude]);
    setSearchQuery(result.fullAddress || result.displayName || '');
    setShowSuggestions(false);
  };

  const handleMapClick = useCallback((lng: number, lat: number) => {
    setMapPosition([lng, lat]);
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  const handleMapLoad = useCallback((map: Map) => {
    setMapInstance(map);
  }, []);

  useEffect(() => {
    loadRestaurant();
    loadWorkingHours();
    apiClient
      .get<{ data: Array<{ id: string; name: string; imageUrl: string | null }> }>('/cuisine-categories')
      .then((res) => setCuisineCategories(res.data.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name || '',
        description: restaurant.description || '',
        phone: restaurant.phone || '',
        address: restaurant.address || '',
        latitude: restaurant.latitude ? Number(restaurant.latitude) : null,
        longitude: restaurant.longitude ? Number(restaurant.longitude) : null,
        minimumOrderAmount: restaurant.minimumOrderAmount || 0,
        deliveryFee: restaurant.deliveryFee || 0,
        estimatedPreparationMinutes: restaurant.estimatedPreparationMinutes || 30,
        cuisineCategoryIds: (restaurant as any).cuisineCategoryIds?.length
          ? (restaurant as any).cuisineCategoryIds
          : (restaurant as any).cuisineCategoryId
            ? [(restaurant as any).cuisineCategoryId]
            : [],
      });
    }
  }, [restaurant]);


  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.latitude || !formData.longitude) {
      setToast({ 
        message: 'Restoran konumu (enlem ve boylam) zorunludur. Lütfen haritadan konum seçin.', 
        type: 'error' 
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateRestaurant({
        name: formData.name,
        description: formData.description,
        phone: formData.phone,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        minimumOrderAmount: formData.minimumOrderAmount,
        deliveryFee: formData.deliveryFee,
        estimatedPreparationMinutes: formData.estimatedPreparationMinutes,
        cuisineCategoryIds: formData.cuisineCategoryIds,
      } as any);
      setToast({ message: 'Restoran bilgileri güncellendi', type: 'success' });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Güncelleme başarısız oldu';
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    e.target.value = '';
    try {
      const { file, wasCompressed, originalSizeMB, compressedSizeMB } = await compressImage(raw, {
        maxWidthOrHeight: 800,
        maxSizeMB: 0.5,
      });
      if (wasCompressed) {
        setToast({
          message: `Logo optimize edildi: ${originalSizeMB.toFixed(1)} MB → ${compressedSizeMB.toFixed(1)} MB`,
          type: 'success',
        });
      }
      await uploadLogo(file);
      setToast({ message: 'Logo başarıyla yüklendi', type: 'success' });
    } catch (err) {
      setToast({ message: 'Logo yüklenirken bir hata oluştu', type: 'error' });
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    e.target.value = '';
    try {
      const { file, wasCompressed, originalSizeMB, compressedSizeMB } = await compressImage(raw, {
        maxWidthOrHeight: 1920,
        maxSizeMB: 1.0,
      });
      if (wasCompressed) {
        setToast({
          message: `Banner optimize edildi: ${originalSizeMB.toFixed(1)} MB → ${compressedSizeMB.toFixed(1)} MB`,
          type: 'success',
        });
      }
      await uploadBanner(file);
      setToast({ message: 'Banner başarıyla yüklendi', type: 'success' });
    } catch (err) {
      setToast({ message: 'Banner yüklenirken bir hata oluştu', type: 'error' });
    }
  };

  const handleToggleAcceptingOrders = async () => {
    if (!restaurant) return;

    try {
      await toggleAcceptingOrders(!restaurant.isAcceptingOrders);
      setToast({
        message: restaurant.isAcceptingOrders
          ? 'Sipariş kabulü kapatıldı'
          : 'Sipariş kabulü açıldı',
        type: 'success',
      });
    } catch (err) {
      setToast({ message: 'Durum güncellenirken bir hata oluştu', type: 'error' });
    }
  };

  if (isLoadingRestaurant) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600">Restoran bilgileri yükleniyor...</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <Card>
        <p className="text-gray-600">Restoran bulunamadı.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      {/* Error Message */}
      {(restaurantError || error) && (
        <Card className="bg-error/10 border border-error/20">
          <p className="text-error">{restaurantError || error}</p>
          <Button size="sm" variant="primary" onClick={() => { clearError(); setError(null); }} className="mt-2">
            Kapat
          </Button>
        </Card>
      )}

      {/* Restaurant Info Form */}
      <Card>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Restoran Bilgileri</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Restoran Adı *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Restoran adı"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Açıklama
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Restoran açıklaması"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <Input
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+905551234567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Restoran Konumu *
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Restoran konumu kurye atama ve mesafe hesaplamaları için zorunludur.
            </p>
            
            <div className="relative mb-2" ref={suggestionsRef}>
              <div className="relative">
                <Input
                  value={searchQuery || formData.address}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleInputChange('address', e.target.value);
                  }}
                  placeholder="Restoran adresini arayın veya haritadan seçin"
                  className="pr-10"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
              
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleAddressSelect(suggestion)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="font-medium text-gray-900">{suggestion.displayName || suggestion.fullAddress}</p>
                      {suggestion.fullAddress && suggestion.fullAddress !== suggestion.displayName && (
                        <p className="text-sm text-gray-500">{suggestion.fullAddress}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {showSuggestions && suggestions.length === 0 && searchQuery.trim() && !isSearching && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                  <p className="text-sm text-gray-500">Sonuç bulunamadı</p>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="h-64 w-full rounded-lg overflow-hidden border border-gray-300">
              {mapPosition && (
                <MapLibreMap
                  center={mapPosition}
                  zoom={15}
                  onMapLoad={handleMapLoad}
                  onMapClick={handleMapClick}
                  className="w-full h-full"
                  showControls={true}
                  showGeolocate={true}
                  showFullscreen={false}
                >
                  <MapMarker
                    position={mapPosition}
                    color="#E63946"
                    size={40}
                    popup="Restoran Konumu"
                    draggable={true}
                    onDragEnd={(position) => {
                      setMapPosition(position);
                      reverseGeocode(position[1], position[0]);
                    }}
                  />
                </MapLibreMap>
              )}
            </div>

            {isReverseGeocoding && (
              <p className="text-xs text-gray-500 mt-1">Adres bilgisi alınıyor...</p>
            )}

            {formData.latitude && formData.longitude && (
              <p className="text-xs text-gray-500 mt-1">
                Konum: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
              </p>
            )}

            {!formData.latitude || !formData.longitude ? (
              <p className="text-xs text-red-600 mt-1">
                ⚠️ Restoran konumu seçilmedi. Lütfen haritadan konum seçin veya adres arayın.
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min. Sipariş (₺)
              </label>
              <NumberInput
                value={formData.minimumOrderAmount === 0 ? '' : String(formData.minimumOrderAmount)}
                onChange={(s) =>
                  handleInputChange('minimumOrderAmount', s === '' ? 0 : parseFloat(s) || 0)
                }
                placeholder="50"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formatPrice(formData.minimumOrderAmount || 0)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teslimat Ücreti (₺)
              </label>
              <NumberInput
                value={formData.deliveryFee === 0 ? '' : String(formData.deliveryFee)}
                onChange={(s) =>
                  handleInputChange('deliveryFee', s === '' ? 0 : parseFloat(s) || 0)
                }
                placeholder="5"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formatPrice(formData.deliveryFee || 0)}
              </p>
            </div>

            <div>
              <CuisineMultiSelect
                label="Mutfak türleri"
                options={cuisineCategories.map((category) => ({
                  id: category.id,
                  name: category.name,
                }))}
                value={formData.cuisineCategoryIds}
                onChange={(cuisineCategoryIds) =>
                  setFormData((prev) => ({ ...prev, cuisineCategoryIds }))
                }
                hint="Birden fazla mutfak seçebilirsiniz. Restoranınız tüm seçili kategorilerde görünür."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hazırlık Süresi (dakika)
              </label>
              <NumberInput
                integerOnly
                value={formData.estimatedPreparationMinutes === 0 ? '' : String(formData.estimatedPreparationMinutes)}
                onChange={(s) =>
                  handleInputChange(
                    'estimatedPreparationMinutes',
                    s === '' ? 30 : parseInt(s, 10) || 30,
                  )
                }
                placeholder="30"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Image Uploads */}
      <div className="grid grid-cols-2 gap-6">
        {/* Logo Upload */}
        <Card>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Logo</h3>
          <div className="space-y-4">
            {restaurant.logoUrl && (
              <div className="w-32 aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={restaurant.logoUrl}
                  alt="Logo"
                  className="w-full h-full object-contain object-center"
                />
              </div>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
              >
                Logo Yükle
              </Button>
            </div>
          </div>
        </Card>

        {/* Banner Upload */}
        <Card>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Banner</h3>
          <div className="space-y-4">
            {restaurant.bannerUrl && (
              <div className="w-full max-w-2xl aspect-video rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={restaurant.bannerUrl}
                  alt="Banner"
                  className="w-full h-full object-contain object-center"
                />
              </div>
            )}
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              onChange={handleBannerUpload}
              className="hidden"
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => bannerInputRef.current?.click()}
              >
                Banner Yükle
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Status Toggle */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Sipariş Durumu</h3>
            <p className="text-sm text-gray-600">
              {restaurant.isAcceptingOrders
                ? 'Restoran şu anda sipariş kabul ediyor'
                : 'Restoran şu anda sipariş kabul etmiyor'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge
              variant={restaurant.isAcceptingOrders ? 'success' : 'danger'}
            >
              {restaurant.isAcceptingOrders ? 'Açık' : 'Kapalı'}
            </Badge>
            <button
              onClick={handleToggleAcceptingOrders}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                restaurant.isAcceptingOrders ? 'bg-primary' : 'bg-gray-300',
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  restaurant.isAcceptingOrders ? 'translate-x-6' : 'translate-x-1',
                )}
              />
            </button>
          </div>
        </div>
      </Card>

      {/* Working Hours */}
      <Card>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Çalışma Saatleri</h3>
        <OperatingHoursForm
          initialHours={useRestaurantStore.getState().workingHours}
          onSave={async (hours) => {
            const { updateWorkingHours } = useRestaurantStore.getState();
            await updateWorkingHours(hours);
            setToast({ message: 'Çalışma saatleri güncellendi', type: 'success' });
          }}
        />
      </Card>

      {/* Service Zone (Read-only) */}
      <Card>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Servis Bölgesi</h3>
        <p className="text-sm text-gray-600">
          Servis bölgesi bilgisi sadece görüntüleme amaçlıdır. Değişiklik için lütfen yönetici ile iletişime geçin.
        </p>
      </Card>
    </div>
  );
};

export default SettingsTab;
